import { useEffect } from "react";
import DataTypes from "~data";
import { EndpointAuth, SubscriptionIssues } from "~services/app/endpointGuards";

import t from "./i18n";
import { useAuthentication } from "./providers/AuthenticationProvider";
import { useModal } from "./providers/ModalProvider";
import useGetSupport from "./use/get-support";
import useRedirect from "./use/redirect";

type ApiErrorReason =
  | "captcha_failure"
  | "mfa_setup_required"
  | "record_not_found"
  | "request_too_large"
  | "unexpected"
  | {
      auth: EndpointAuth;
    }
  | {
      subscriptionIssues: SubscriptionIssues;
      teamId?: DataTypes["Teams"]["id"];
    };

const requiredRoleDescriptions: Record<EndpointAuth, string> = {
  "admin": t("You must be an admin to perform this action."),
  "admin:accounts_view": t("Please ask the tech team for accounts view access to continue."),
  "admin:accounts_write": t("Please ask the tech team for accounts write access to continue."),
  "admin:customer_success": t("Only Customer Success Managers can perform this action."),
  "admin:devops": t("Please ask the tech team for Devops access to continue."),
  "admin:impersonate": t("Please ask the tech team for impersonation access to continue."),
  "admin:product_cobrowsingapi": t("You are a cobrowsingapi admin."),
  "admin:product_helloscreen": t("You are a helloscreen admin."),
  "admin:product_livedocument": t("You are a livedocument admin."),
  "admin:product_userview": t("You are a userview admin."),
  "admin:sales_exec": t("Only Sales Executives can perform this action."),
  "admin:sales_lead_target": t("Please ask the tech team for sales lead target access to continue."),
  "admin:sales_manager": t("Only Sales Managers can perform this action."),
  "admin:sales_view": t("Please ask the tech team for sales view access to continue."),
  "admin:sales_write": t("Please ask the tech team for sales write access to continue."),
  "admin:superuser": t("Only super admins can perform this action."),
  "guest": t("You are already logged in. Please log out to perform this action."),
  "member": t("You must be a member of this team to perform this action."),
  "member:canAccessSettings": t("Please ask your admin for settings access to continue."),
  "member:canManageBilling": t("Please ask your admin for billing access to continue."),
  "member:canManageContent": t("Please ask your admin for content management access to continue."),
  "member:canManageUsers": t("Please ask your admin for user management access to continue."),
  "member:canPurchase": t("Please ask your admin for billing access to continue."),
  "member:canUse": t("Please ask your admin for access to continue."),
  "member:canViewReporting": t("Please ask your admin for reporting access to continue."),
  "owner": t("You must be the owner of this team to perform this action."),
  "public": "NA",
  "user_any_team": t("You must be logged in to perform this action."),
};

const subscriptionStatusDescriptions: Record<
  Exclude<SubscriptionIssues["requiredSubscriptionStatus"], undefined>,
  string
> = {
  "active": t(
    "Your subscription is currently inactive. Please reach out to your account manager to reactivate your subscription.",
  ),
  "active:subscribed": t("You can only perform this action if you have an active subscription."),
  "active:trial": t("You can only perform this action if you are on trial."),
  "inactive": t("You already have an active subscription."),
};

export default function ApiErrorsListener({ children }: { children: JSX.Element }) {
  const { confirm } = useModal();
  const redirect = useRedirect();
  const { teams } = useAuthentication();
  const { getSupport } = useGetSupport();

  useEffect(() => {
    const listener = (event: CustomEvent<{ reason: ApiErrorReason }>) => {
      if (event.detail.reason === "request_too_large") {
        void confirm({
          afterClose: () => {
            window.reload();
          },
          content: t(
            "Unfortunately your browser made a request too large, and our server could not process it. Please try again.",
          ),
          title: t("Request too large"),
        });
      } else if (event.detail.reason === "unexpected") {
        confirmUnexpectedError(confirm);
      } else if (event.detail.reason === "record_not_found") {
        void confirm({
          afterClose: () => {
            redirect(["up.root", {}]);
          },
          content: t("We could not find this page. Please contact us if you think this is an error."),
          title: t("Not found"),
          type: "info",
        });
      } else if (event.detail.reason === "captcha_failure") {
        void confirm({
          afterClose: () => {
            getSupport();
          },
          content: t(
            "Our systems have detected unusual activity from your browser. Please contact our team for assistance.",
          ),
          title: t("Please contact our team"),
          type: "warning",
        });
      } else if (event.detail.reason === "mfa_setup_required") {
        void confirm({
          afterClose: (accept) => {
            if (accept) redirect(["up.auth.mfa.enable"]);
            else window.reload();
          },
          cancelText: t("Not now"),
          confirmText: t("Set up two-factor authentication"),
          content: t("You must set up two-factor authentication before performing this action."),
          replace: true,
          title: t("Two-factor authentication required"),
          type: "warning",
        });
      } else if ("auth" in event.detail.reason) {
        const requiredRole = event.detail.reason.auth;
        void confirm({
          afterClose: () => {
            window.reload();
          },
          content: requiredRoleDescriptions[requiredRole],
          title: t("Not authorized"),
        });
      } else if ("subscriptionIssues" in event.detail.reason) {
        const { banned, missingFeatures, requiredSubscriptionStatus } =
          event.detail.reason.subscriptionIssues;

        if (banned) {
          void confirm({
            afterClose: () => {
              getSupport();
            },
            confirmText: t("Contact us"),
            content: t(
              "Our team has been trying to contact you, but was unable to reach you. Please get in touch to resume usage.",
            ),
            title: t("Please contact our team"),
          });
        } else if (missingFeatures) {
          void confirm({
            afterClose: () => {
              const { teamId } = event.detail.reason as Extract<
                ApiErrorReason,
                { subscriptionIssues: SubscriptionIssues }
              >;
              const team = teamId ? teams?.find((t) => t.id === teamId) : undefined;
              if (team)
                redirect(["up.settings.billing.upgrade", { feature: missingFeatures[0]!, teamDomain: team.domain }]);
            },
            content: t(
              "Your subscription does not currently have access to this feature. Please reach out to your account manager to upgrade your account.",
            ),
            title: t("Upgrade required"),
          });
        } else if (requiredSubscriptionStatus) {
          const { teamId } = event.detail.reason;
          const team = teamId ? teams?.find((t) => t.id === teamId) : undefined;
          void confirm({
            afterClose: () => {
              if (requiredSubscriptionStatus.startsWith("active")) {
                if (team) redirect(["up.onboarding.purchase", { teamDomain: team.domain }]);
                else redirect(["up.shortCuts.purchase"]);
              } else {
                redirect(["up.root", {}]);
              }
            },
            content: subscriptionStatusDescriptions[requiredSubscriptionStatus],
            title: t("Invalid subscription status"),
          });
        }
      } else {
        void confirm({
          content: t("An unexpected error has occurred. Please try again later."),
          title: t("Unexpected error"),
        });
      }
    };
    document.addEventListener("apierror", listener as EventListener);

    return () => {
      document.removeEventListener("apierror", listener as EventListener);
    };
  }, [confirm, redirect, teams, getSupport]);

  return children;
}

export function confirmUnexpectedError(confirm: ReturnType<typeof useModal>["confirm"]) {
  void confirm({
    afterClose: () => {
      window.reload();
    },
    content: t("Unfortunately we have encountered an unexpected error. Our team has been notified. Please try again."),
    title: t("Unexpected error"),
  });
}

export function makeApiErrorEvent(reason: ApiErrorReason) {
  return new CustomEvent<{ reason: ApiErrorReason }>("apierror", {
    detail: {
      reason,
    },
  });
}
